import { useState, useEffect } from "react";
import { Menu, X, ChevronRight, Shield, Users, Building2, FileCheck, ArrowRight, Phone, Mail, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Official brand images
const IMAGES = {
  logo: "https://customer-assets.emergentagent.com/job_estate-ops-review/artifacts/7b20sfi6_90A8CD8C-9108-40F7-A4AE-AE5078F81809.png",
  hero: "https://customer-assets.emergentagent.com/job_estate-ops-review/artifacts/vi6ewrk4_5AEBD63D-DCDF-4B95-95FC-D4E38D06D5BD.jpeg", // Pool/estate exterior
  about: "https://customer-assets.emergentagent.com/job_estate-ops-review/artifacts/dpaik5bl_5AEBD63D-DCDF-4B95-95FC-D4E38D06D5BD.jpeg", // Wine cellar
  service1: "https://customer-assets.emergentagent.com/job_estate-ops-review/artifacts/vlja6rhg_5AEBD63D-DCDF-4B95-95FC-D4E38D06D5BD.jpeg", // Home theater
  service2: "https://customer-assets.emergentagent.com/job_estate-ops-review/artifacts/e7t30tr8_5AEBD63D-DCDF-4B95-95FC-D4E38D06D5BD.jpeg", // Luxury garage
  partner: "https://customer-assets.emergentagent.com/job_estate-ops-review/artifacts/oxahz9ma_5AEBD63D-DCDF-4B95-95FC-D4E38D06D5BD.jpeg", // Living room with chandelier
  contact: "https://customer-assets.emergentagent.com/job_estate-ops-review/artifacts/yxkwihft_5AEBD63D-DCDF-4B95-95FC-D4E38D06D5BD.jpeg", // Luxury closet/dressing room
  lounge: "https://customer-assets.emergentagent.com/job_estate-ops-review/artifacts/8ztjnqtt_5AEBD63D-DCDF-4B95-95FC-D4E38D06D5BD.jpeg", // Dark lounge/club room
};

// Navigation Component
const Navigation = ({ scrolled }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const navLinks = [
    { name: "About", href: "#about" },
    { name: "Services", href: "#services" },
    { name: "Partners", href: "#partners" },
    { name: "Contact", href: "#contact" },
  ];

  const scrollToSection = (href) => {
    setMobileMenuOpen(false);
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? "glass-header shadow-sm" : "bg-transparent"
      }`}
      data-testid="main-navigation"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <nav className="flex items-center justify-between h-20">
          {/* Logo */}
          <a
            href="#"
            onClick={(e) => {
              e.preventDefault();
              window.scrollTo({ top: 0, behavior: "smooth" });
            }}
            className="flex items-center"
            data-testid="logo-link"
          >
            <img 
              src={IMAGES.logo} 
              alt="Estate Benchmark Authority" 
              className={`h-14 w-auto transition-all duration-300 ${scrolled ? "mix-blend-multiply" : "mix-blend-lighten"}`}
            />
          </a>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-10">
            {navLinks.map((link) => (
              <button
                key={link.name}
                onClick={() => scrollToSection(link.href)}
                className={`link-underline text-xs uppercase tracking-[0.2em] font-medium transition-colors duration-300 ${
                  scrolled ? "text-[#0B1221] hover:text-[#C5A059]" : "text-white/90 hover:text-[#C5A059]"
                }`}
                data-testid={`nav-${link.name.toLowerCase()}`}
              >
                {link.name}
              </button>
            ))}
            <Button
              onClick={() => scrollToSection("#contact")}
              className="bg-[#0B1221] text-white hover:bg-[#C5A059] hover:text-[#0B1221] rounded-none px-6 py-2 text-xs uppercase tracking-widest font-medium transition-all duration-300"
              data-testid="nav-consultation-btn"
            >
              Request Consultation
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-toggle"
          >
            {mobileMenuOpen ? (
              <X className={scrolled ? "text-[#0B1221]" : "text-white"} size={24} />
            ) : (
              <Menu className={scrolled ? "text-[#0B1221]" : "text-white"} size={24} />
            )}
          </button>
        </nav>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden absolute top-20 left-0 right-0 bg-white border-b border-[#E2E2E2] shadow-lg animate-fade-in" data-testid="mobile-menu">
            <div className="p-6 space-y-4">
              {navLinks.map((link) => (
                <button
                  key={link.name}
                  onClick={() => scrollToSection(link.href)}
                  className="block w-full text-left text-[#0B1221] text-sm uppercase tracking-widest font-medium py-3 border-b border-[#E2E2E2] hover:text-[#C5A059] transition-colors"
                  data-testid={`mobile-nav-${link.name.toLowerCase()}`}
                >
                  {link.name}
                </button>
              ))}
              <Button
                onClick={() => scrollToSection("#contact")}
                className="w-full bg-[#0B1221] text-white hover:bg-[#C5A059] hover:text-[#0B1221] rounded-none py-4 text-xs uppercase tracking-widest font-medium mt-4"
                data-testid="mobile-consultation-btn"
              >
                Request Consultation
              </Button>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

// Hero Section
const HeroSection = () => {
  return (
    <section className="relative min-h-screen flex items-center" data-testid="hero-section">
      {/* Background Image */}
      <div className="absolute inset-0">
        <img
          src={IMAGES.hero}
          alt="Luxury Estate"
          className="w-full h-full object-cover"
        />
        <div className="hero-overlay absolute inset-0" />
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-12 py-32">
        <div className="max-w-3xl">
          <p className="text-[#C5A059] text-xs uppercase tracking-[0.3em] font-medium mb-6 animate-fade-in-up opacity-0" style={{ animationDelay: "0.1s", animationFillMode: "forwards" }}>
            Your Estate. Independently Evaluated.
          </p>
          <h1 className="font-heading text-5xl md:text-7xl text-white font-medium tracking-tight leading-[1.1] mb-8 animate-fade-in-up opacity-0" style={{ animationDelay: "0.2s", animationFillMode: "forwards" }}>
            Has Anyone Actually Audited How Your Estate{" "}
            <span className="text-[#C5A059]">Operates?</span>
          </h1>
          <p className="text-white/80 text-lg md:text-xl leading-relaxed mb-10 max-w-2xl animate-fade-in-up opacity-0" style={{ animationDelay: "0.3s", animationFillMode: "forwards" }}>
            Estate Benchmark Authority provides confidential, independent operational assessments for UHNW households — so ownership has a clear, honest picture of how their estate is truly performing.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 animate-fade-in-up opacity-0" style={{ animationDelay: "0.4s", animationFillMode: "forwards" }}>
            <Button
              onClick={() => document.querySelector("#contact")?.scrollIntoView({ behavior: "smooth" })}
              className="bg-[#C5A059] text-[#0B1221] hover:bg-white rounded-none px-8 py-6 text-xs uppercase tracking-widest font-bold transition-all duration-300 group"
              data-testid="hero-consultation-btn"
            >
              Request Confidential Consultation
              <ChevronRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
            </Button>
            <Button
              onClick={() => document.querySelector("#services")?.scrollIntoView({ behavior: "smooth" })}
              variant="outline"
              className="bg-transparent border border-white/40 text-white hover:bg-white hover:text-[#0B1221] rounded-none px-8 py-6 text-xs uppercase tracking-widest font-bold transition-all duration-300"
              data-testid="hero-services-btn"
            >
              View Our Services
            </Button>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-10 left-1/2 -translate-x-1/2 animate-bounce">
        <div className="w-[1px] h-16 bg-gradient-to-b from-[#C5A059] to-transparent" />
      </div>
    </section>
  );
};

// About Section
const AboutSection = () => {
  return (
    <section id="about" className="py-24 md:py-32 bg-[#F9F9F7] grid-pattern relative" data-testid="about-section">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Image */}
          <div className="relative animate-fade-in-up opacity-0" style={{ animationDelay: "0.1s", animationFillMode: "forwards" }}>
            <div className="aspect-[4/3] overflow-hidden">
              <img
                src={IMAGES.about}
                alt="Architectural blueprints"
                className="w-full h-full object-cover image-hover-effect"
              />
            </div>
            <div className="absolute -bottom-6 -right-6 w-32 h-32 border-2 border-[#C5A059] -z-10" />
          </div>

          {/* Content */}
          <div className="animate-fade-in-up opacity-0" style={{ animationDelay: "0.2s", animationFillMode: "forwards" }}>
            <p className="text-[#C5A059] text-xs uppercase tracking-[0.3em] font-bold mb-4">
              Our Philosophy
            </p>
            <h2 className="font-heading text-4xl md:text-5xl text-[#0B1221] font-medium tracking-tight leading-tight mb-8">
              Most Estate Problems Are Invisible Until They're Expensive
            </h2>
            <div className="space-y-6 text-[#64748B] leading-relaxed">
              <p>
                Staff overlap. Vendor overcharges. Undefined roles. Performance gaps that no one on the inside will surface on their own.
              </p>
              <p>
                Estate Benchmark Authority comes in as a neutral third party — no agenda, no placement fees, no conflicts of interest — and gives ownership the honest operational picture they deserve.
              </p>
            </div>

            {/* Values */}
            <div className="grid grid-cols-2 gap-6 mt-10">
              {[
                { icon: Shield, label: "Independence" },
                { icon: Users, label: "Discretion" },
                { icon: Building2, label: "Expertise" },
                { icon: FileCheck, label: "Analysis" },
              ].map((item, index) => (
                <div
                  key={item.label}
                  className="flex items-center gap-3 p-4 border border-[#E2E2E2] hover:border-[#C5A059]/30 transition-colors duration-300"
                >
                  <item.icon className="w-5 h-5 text-[#C5A059]" />
                  <span className="text-[#0B1221] text-sm font-medium">{item.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Who This Is For Section
const WhoThisIsForSection = () => {
  return (
    <section className="py-24 md:py-32 bg-white" data-testid="who-this-is-for-section">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="text-center mb-16 animate-fade-in-up opacity-0" style={{ animationDelay: "0.1s", animationFillMode: "forwards" }}>
          <p className="text-[#C5A059] text-xs uppercase tracking-[0.3em] font-bold mb-4">
            Who This Is For
          </p>
          <h2 className="font-heading text-4xl md:text-5xl text-[#0B1221] font-medium tracking-tight leading-tight">
            Two Ways to Work With Us
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12">
          {/* For Principals & Family Offices */}
          <div className="p-10 md:p-12 bg-[#F9F9F7] border border-[#E2E2E2] hover:border-[#C5A059]/30 transition-all duration-300 animate-fade-in-up opacity-0" style={{ animationDelay: "0.2s", animationFillMode: "forwards" }}>
            <div className="w-12 h-12 flex items-center justify-center border border-[#C5A059] mb-6">
              <Building2 className="w-6 h-6 text-[#C5A059]" />
            </div>
            <h3 className="font-heading text-2xl md:text-3xl text-[#0B1221] font-medium mb-4">
              For Principals & Family Offices
            </h3>
            <p className="text-[#64748B] leading-relaxed">
              Know exactly how your estate is performing — before a new hire, after a transition, or anytime you want an objective outside perspective.
            </p>
          </div>

          {/* For Recruiters & Placement Partners */}
          <div className="p-10 md:p-12 bg-[#F9F9F7] border border-[#E2E2E2] hover:border-[#C5A059]/30 transition-all duration-300 animate-fade-in-up opacity-0" style={{ animationDelay: "0.3s", animationFillMode: "forwards" }}>
            <div className="w-12 h-12 flex items-center justify-center border border-[#C5A059] mb-6">
              <Users className="w-6 h-6 text-[#C5A059]" />
            </div>
            <h3 className="font-heading text-2xl md:text-3xl text-[#0B1221] font-medium mb-4">
              For Recruiters & Placement Partners
            </h3>
            <p className="text-[#64748B] leading-relaxed">
              Offer your clients something no other firm provides. We partner with select recruiters and pay a referral fee for every introduction that leads to an engagement.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

// Services Section
const ServicesSection = () => {
  const assessmentAreas = [
    "Staffing Structure Review",
    "Vendor Relationships Analysis",
    "Operational Workflows Assessment",
    "Purchasing Practices Evaluation",
    "Technology Infrastructure Review",
    "Maintenance Programs Analysis",
    "Administrative Procedures Audit",
    "Operational Expenses Review",
  ];

  const processSteps = [
    {
      step: "01",
      title: "Confidential Consultation",
      description: "Initial discussion to understand estate scope, operational concerns, and assessment objectives.",
    },
    {
      step: "02",
      title: "Information Gathering",
      description: "Comprehensive collection of operational data, staffing records, vendor contracts, and financial information.",
    },
    {
      step: "03",
      title: "Operational Review",
      description: "In-depth analysis of estate operations, identifying inefficiencies, redundancies, and optimization opportunities.",
    },
    {
      step: "04",
      title: "Findings Report",
      description: "Detailed report with observations, recommendations, and actionable strategies for operational improvement.",
    },
  ];

  return (
    <section id="services" className="py-24 md:py-32 bg-white blueprint-section" data-testid="services-section">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        {/* Header */}
        <div className="text-center mb-20 animate-fade-in-up opacity-0" style={{ animationDelay: "0.1s", animationFillMode: "forwards" }}>
          <p className="text-[#C5A059] text-xs uppercase tracking-[0.3em] font-bold mb-4">
            Our Services
          </p>
          <h2 className="font-heading text-4xl md:text-5xl text-[#0B1221] font-medium tracking-tight leading-tight mb-6">
            Estate Operational Assessment
          </h2>
          <p className="text-[#64748B] text-lg max-w-2xl mx-auto leading-relaxed">
            A comprehensive review providing visibility, accountability, and operational clarity 
            for informed decision-making.
          </p>
        </div>

        {/* Bento Grid Layout */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8 mb-20">
          {/* Main Service Card */}
          <div className="md:col-span-7 bg-[#0B1221] p-10 md:p-14 relative overflow-hidden animate-fade-in-up opacity-0" style={{ animationDelay: "0.2s", animationFillMode: "forwards" }}>
            <div className="absolute top-0 right-0 w-64 h-64 bg-[#C5A059]/5 rounded-full blur-3xl" />
            <p className="text-[#C5A059] text-xs uppercase tracking-[0.3em] font-bold mb-4">
              Core Assessment
            </p>
            <h3 className="font-heading text-3xl md:text-4xl text-white font-medium mb-6">
              Comprehensive Operational Review
            </h3>
            <p className="text-white/70 leading-relaxed mb-8">
              Our assessment evaluates how your estate is managed, identifies inefficiencies, 
              and provides a structured report containing observations and recommendations for 
              optimizing operations.
            </p>
            <div className="grid grid-cols-2 gap-4">
              {assessmentAreas.slice(0, 4).map((area) => (
                <div key={area} className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 bg-[#C5A059]" />
                  <span className="text-white/80 text-sm">{area}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Side Image */}
          <div className="md:col-span-5 animate-fade-in-up opacity-0" style={{ animationDelay: "0.3s", animationFillMode: "forwards" }}>
            <div className="h-full min-h-[300px] overflow-hidden">
              <img
                src={IMAGES.service1}
                alt="Estate assessment"
                className="w-full h-full object-cover image-hover-effect"
              />
            </div>
          </div>

          {/* Assessment Areas List */}
          <div className="md:col-span-5 bg-[#F9F9F7] p-10 border border-[#E2E2E2] animate-fade-in-up opacity-0" style={{ animationDelay: "0.4s", animationFillMode: "forwards" }}>
            <h4 className="font-heading text-2xl text-[#0B1221] font-medium mb-6">
              Assessment Areas
            </h4>
            <div className="space-y-4">
              {assessmentAreas.map((area, index) => (
                <div
                  key={area}
                  className="flex items-center gap-4 pb-4 border-b border-[#E2E2E2] last:border-0 group"
                >
                  <span className="text-[#C5A059] text-xs font-mono">
                    {String(index + 1).padStart(2, "0")}
                  </span>
                  <span className="text-[#0B1221] group-hover:text-[#C5A059] transition-colors duration-300">
                    {area}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Report Output */}
          <div className="md:col-span-7 bg-white p-10 border border-[#E2E2E2] animate-fade-in-up opacity-0" style={{ animationDelay: "0.5s", animationFillMode: "forwards" }}>
            <h4 className="font-heading text-2xl text-[#0B1221] font-medium mb-6">
              Structured Report Deliverables
            </h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {[
                { title: "Operational Observations", desc: "Detailed findings on current estate operations" },
                { title: "Inefficiency Indicators", desc: "Identification of cost and process redundancies" },
                { title: "Structural Recommendations", desc: "Actionable strategies for improvement" },
                { title: "Vendor Optimization", desc: "Analysis of vendor relationships and contracts" },
                { title: "Staffing Alignment", desc: "Assessment of roles and responsibilities" },
                { title: "Executive Summary", desc: "High-level overview for ownership review" },
              ].map((item) => (
                <div key={item.title} className="gold-accent-border pl-4">
                  <h5 className="text-[#0B1221] font-medium mb-1">{item.title}</h5>
                  <p className="text-[#64748B] text-sm">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Process Steps */}
        <div className="mt-20">
          <h3 className="font-heading text-3xl text-[#0B1221] font-medium text-center mb-12">
            Assessment Process
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {processSteps.map((step, index) => (
              <div
                key={step.step}
                className="relative p-8 bg-[#F9F9F7] border border-[#E2E2E2] hover:border-[#C5A059]/30 transition-all duration-300 animate-fade-in-up opacity-0"
                style={{ animationDelay: `${0.2 + index * 0.1}s`, animationFillMode: "forwards" }}
              >
                <span className="font-heading text-5xl text-[#C5A059]/20 font-bold absolute top-4 right-4">
                  {step.step}
                </span>
                <h4 className="font-heading text-xl text-[#0B1221] font-medium mb-4 relative z-10">
                  {step.title}
                </h4>
                <p className="text-[#64748B] text-sm leading-relaxed relative z-10">
                  {step.description}
                </p>
                {index < processSteps.length - 1 && (
                  <ArrowRight className="hidden md:block absolute top-1/2 -right-6 w-4 h-4 text-[#C5A059]" />
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

// Use Cases Section
const UseCasesSection = () => {
  const useCases = [
    "Estate inheritance transitions",
    "Major staffing changes",
    "Hiring of a new estate manager",
    "Rising estate operating costs",
    "Restructuring of estate operations",
    "Family office reviews of estate spending",
  ];

  return (
    <section className="py-24 md:py-32 bg-[#0B1221] relative overflow-hidden" data-testid="use-cases-section">
      <div className="absolute inset-0 grid-pattern opacity-20" />
      
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div className="animate-fade-in-up opacity-0" style={{ animationDelay: "0.1s", animationFillMode: "forwards" }}>
            <p className="text-[#C5A059] text-xs uppercase tracking-[0.3em] font-bold mb-4">
              When to Consider
            </p>
            <h2 className="font-heading text-4xl md:text-5xl text-white font-medium tracking-tight leading-tight mb-8">
              Ideal Assessment Scenarios
            </h2>
            <p className="text-white/70 text-lg leading-relaxed mb-10">
              Our operational assessments are particularly valuable during periods of transition, 
              review, or when seeking greater visibility into estate operations.
            </p>
            
            <div className="space-y-4">
              {useCases.map((useCase, index) => (
                <div
                  key={useCase}
                  className="flex items-center gap-4 p-4 border border-white/10 hover:border-[#C5A059]/50 transition-all duration-300 group"
                >
                  <div className="w-8 h-8 flex items-center justify-center border border-[#C5A059]">
                    <span className="text-[#C5A059] text-xs font-mono">
                      {String(index + 1).padStart(2, "0")}
                    </span>
                  </div>
                  <span className="text-white/90 group-hover:text-[#C5A059] transition-colors duration-300">
                    {useCase}
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div className="relative animate-fade-in-up opacity-0" style={{ animationDelay: "0.2s", animationFillMode: "forwards" }}>
            <div className="aspect-[4/3] overflow-hidden">
              <img
                src={IMAGES.service2}
                alt="Luxury estate"
                className="w-full h-full object-cover image-hover-effect"
              />
            </div>
            <div className="absolute -top-6 -left-6 w-32 h-32 border-2 border-[#C5A059] -z-10" />
          </div>
        </div>
      </div>
    </section>
  );
};

// Partners Section (Recruiters)
const PartnersSection = () => {
  const benefits = [
    {
      title: "Better Hiring Decisions",
      description: "Understand the true operational needs before placing estate leadership.",
    },
    {
      title: "Reduced Failed Placements",
      description: "Align candidate qualifications with actual estate requirements.",
    },
    {
      title: "Clearer Staffing Structure",
      description: "Provide candidates with accurate role expectations and responsibilities.",
    },
    {
      title: "Improved Transparency",
      description: "Offer clients valuable operational insights alongside recruitment services.",
    },
  ];

  return (
    <section id="partners" className="py-24 md:py-32 bg-[#F9F9F7]" data-testid="partners-section">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          {/* Image */}
          <div className="relative order-2 lg:order-1 animate-fade-in-up opacity-0" style={{ animationDelay: "0.1s", animationFillMode: "forwards" }}>
            <div className="aspect-[4/3] overflow-hidden">
              <img
                src={IMAGES.partner}
                alt="Professional office"
                className="w-full h-full object-cover image-hover-effect"
              />
            </div>
          </div>

          {/* Content */}
          <div className="order-1 lg:order-2 animate-fade-in-up opacity-0" style={{ animationDelay: "0.2s", animationFillMode: "forwards" }}>
            <p className="text-[#C5A059] text-xs uppercase tracking-[0.3em] font-bold mb-4">
              Recruiter Partnership
            </p>
            <h2 className="font-heading text-4xl md:text-5xl text-[#0B1221] font-medium tracking-tight leading-tight mb-8">
              Enhanced Placement Success
            </h2>
            <p className="text-[#64748B] text-lg leading-relaxed mb-10">
              Estate Benchmark Authority partners with recruitment firms to provide operational assessments 
              before estate leadership placements. This ensures staffing levels align with the estate's 
              actual operational needs.
            </p>

            <div className="space-y-6">
              {benefits.map((benefit, index) => (
                <div
                  key={benefit.title}
                  className="p-6 bg-white border border-[#E2E2E2] hover:border-[#C5A059]/30 transition-all duration-300"
                >
                  <div className="flex items-start gap-4">
                    <span className="text-[#C5A059] text-xs font-mono mt-1">
                      {String(index + 1).padStart(2, "0")}
                    </span>
                    <div>
                      <h4 className="text-[#0B1221] font-medium mb-2">{benefit.title}</h4>
                      <p className="text-[#64748B] text-sm leading-relaxed">{benefit.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-10">
              <Button
                onClick={() => document.querySelector("#contact")?.scrollIntoView({ behavior: "smooth" })}
                className="bg-[#0B1221] text-white hover:bg-[#C5A059] hover:text-[#0B1221] rounded-none px-8 py-6 text-xs uppercase tracking-widest font-bold transition-all duration-300"
                data-testid="partner-contact-btn"
              >
                Discuss Partnership Opportunities
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Contact Section
const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    property_region: "",
    estate_size: "",
    staff_count: "",
    inquiry_type: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.property_region || !formData.estate_size || !formData.staff_count || !formData.inquiry_type) {
      toast.error("Please complete all required fields");
      return;
    }

    setIsSubmitting(true);

    try {
      await axios.post(`${API}/inquiries`, formData);
      toast.success("Your inquiry has been submitted. We will contact you shortly.");
      setFormData({
        name: "",
        email: "",
        phone: "",
        property_region: "",
        estate_size: "",
        staff_count: "",
        inquiry_type: "",
        message: "",
      });
    } catch (error) {
      console.error("Error submitting inquiry:", error);
      toast.error("There was an error submitting your inquiry. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      {/* About EBA Section */}
      <section className="py-24 md:py-32 bg-[#0B1221] relative overflow-hidden" data-testid="about-eba-section">
        <div className="absolute inset-0 grid-pattern opacity-20" />
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 relative z-10">
          <div className="max-w-3xl mx-auto text-center animate-fade-in-up opacity-0" style={{ animationDelay: "0.1s", animationFillMode: "forwards" }}>
            <p className="text-[#C5A059] text-xs uppercase tracking-[0.3em] font-bold mb-4">
              About Estate Benchmark Authority
            </p>
            <h2 className="font-heading text-4xl md:text-5xl text-white font-medium tracking-tight leading-tight mb-8">
              The Person Behind the Assessment
            </h2>
            <div className="space-y-6 text-white/70 text-lg leading-relaxed">
              <p>
                <span className="text-white font-medium">Rick Rodriguez</span> founded Estate Benchmark Authority after 15 years working inside high-end private estate and residential operations. Having seen firsthand how even well-staffed, well-resourced estates develop blind spots over time, the goal was simple: create a neutral, independent service that gives ownership the honest operational picture they can't always get from the inside.
              </p>
              <p>
                All assessments are conducted with complete confidentiality and delivered as a structured written report with clear, actionable recommendations.
              </p>
            </div>
            <div className="mt-10 pt-10 border-t border-white/10">
              <div className="flex items-center justify-center gap-8">
                <div className="text-center">
                  <p className="font-heading text-4xl text-[#C5A059] font-medium">15+</p>
                  <p className="text-white/50 text-sm uppercase tracking-widest mt-1">Years Experience</p>
                </div>
                <div className="w-px h-16 bg-white/20" />
                <div className="text-center">
                  <p className="font-heading text-4xl text-[#C5A059] font-medium">100%</p>
                  <p className="text-white/50 text-sm uppercase tracking-widest mt-1">Confidential</p>
                </div>
                <div className="w-px h-16 bg-white/20" />
                <div className="text-center">
                  <p className="font-heading text-4xl text-[#C5A059] font-medium">0</p>
                  <p className="text-white/50 text-sm uppercase tracking-widest mt-1">Conflicts of Interest</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section id="contact" className="py-24 md:py-32 bg-white" data-testid="contact-section">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          {/* Form */}
          <div className="animate-fade-in-up opacity-0" style={{ animationDelay: "0.1s", animationFillMode: "forwards" }}>
            <p className="text-[#C5A059] text-xs uppercase tracking-[0.3em] font-bold mb-4">
              Confidential Inquiry
            </p>
            <h2 className="font-heading text-4xl md:text-5xl text-[#0B1221] font-medium tracking-tight leading-tight mb-4">
              Request a Consultation
            </h2>
            <p className="text-[#64748B] leading-relaxed mb-10">
              All inquiries are handled with the utmost discretion. Please provide details about your estate 
              for an initial assessment discussion.
            </p>

            <form onSubmit={handleSubmit} className="space-y-8" data-testid="inquiry-form">
              {/* Name & Email Row */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="text-xs uppercase tracking-widest text-[#64748B] mb-2 block">
                    Full Name *
                  </label>
                  <Input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Your name"
                    className="dossier-input w-full rounded-none border-0 border-b border-[#E2E2E2] focus:border-[#C5A059] bg-transparent px-0"
                    data-testid="input-name"
                    required
                  />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest text-[#64748B] mb-2 block">
                    Email Address *
                  </label>
                  <Input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="your@email.com"
                    className="dossier-input w-full rounded-none border-0 border-b border-[#E2E2E2] focus:border-[#C5A059] bg-transparent px-0"
                    data-testid="input-email"
                    required
                  />
                </div>
              </div>

              {/* Phone */}
              <div>
                <label className="text-xs uppercase tracking-widest text-[#64748B] mb-2 block">
                  Phone Number
                </label>
                <Input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="+1 (555) 000-0000"
                  className="dossier-input w-full rounded-none border-0 border-b border-[#E2E2E2] focus:border-[#C5A059] bg-transparent px-0"
                  data-testid="input-phone"
                />
              </div>

              {/* Property Region & Estate Size */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="text-xs uppercase tracking-widest text-[#64748B] mb-2 block">
                    Property Region *
                  </label>
                  <Select
                    value={formData.property_region}
                    onValueChange={(value) => handleSelectChange("property_region", value)}
                  >
                    <SelectTrigger className="rounded-none border-0 border-b border-[#E2E2E2] focus:border-[#C5A059] bg-transparent px-0 h-12" data-testid="select-region">
                      <SelectValue placeholder="Select region" />
                    </SelectTrigger>
                    <SelectContent className="rounded-none">
                      <SelectItem value="hamptons">The Hamptons</SelectItem>
                      <SelectItem value="beverly-hills">Beverly Hills</SelectItem>
                      <SelectItem value="palm-beach">Palm Beach</SelectItem>
                      <SelectItem value="aspen">Aspen</SelectItem>
                      <SelectItem value="napa">Napa Valley</SelectItem>
                      <SelectItem value="european">European Villa</SelectItem>
                      <SelectItem value="caribbean">Caribbean</SelectItem>
                      <SelectItem value="multi-property">Multi-Property Portfolio</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest text-[#64748B] mb-2 block">
                    Estate Size *
                  </label>
                  <Select
                    value={formData.estate_size}
                    onValueChange={(value) => handleSelectChange("estate_size", value)}
                  >
                    <SelectTrigger className="rounded-none border-0 border-b border-[#E2E2E2] focus:border-[#C5A059] bg-transparent px-0 h-12" data-testid="select-size">
                      <SelectValue placeholder="Select size" />
                    </SelectTrigger>
                    <SelectContent className="rounded-none">
                      <SelectItem value="under-10000">Under 10,000 sq ft</SelectItem>
                      <SelectItem value="10000-25000">10,000 - 25,000 sq ft</SelectItem>
                      <SelectItem value="25000-50000">25,000 - 50,000 sq ft</SelectItem>
                      <SelectItem value="over-50000">Over 50,000 sq ft</SelectItem>
                      <SelectItem value="compound">Estate Compound</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Staff Count & Inquiry Type */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="text-xs uppercase tracking-widest text-[#64748B] mb-2 block">
                    Number of Staff *
                  </label>
                  <Select
                    value={formData.staff_count}
                    onValueChange={(value) => handleSelectChange("staff_count", value)}
                  >
                    <SelectTrigger className="rounded-none border-0 border-b border-[#E2E2E2] focus:border-[#C5A059] bg-transparent px-0 h-12" data-testid="select-staff">
                      <SelectValue placeholder="Select range" />
                    </SelectTrigger>
                    <SelectContent className="rounded-none">
                      <SelectItem value="1-5">1 - 5 staff</SelectItem>
                      <SelectItem value="6-15">6 - 15 staff</SelectItem>
                      <SelectItem value="16-30">16 - 30 staff</SelectItem>
                      <SelectItem value="30+">30+ staff</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest text-[#64748B] mb-2 block">
                    Inquiry Type *
                  </label>
                  <Select
                    value={formData.inquiry_type}
                    onValueChange={(value) => handleSelectChange("inquiry_type", value)}
                  >
                    <SelectTrigger className="rounded-none border-0 border-b border-[#E2E2E2] focus:border-[#C5A059] bg-transparent px-0 h-12" data-testid="select-inquiry-type">
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent className="rounded-none">
                      <SelectItem value="operational-assessment">Operational Assessment</SelectItem>
                      <SelectItem value="staffing-review">Staffing Review</SelectItem>
                      <SelectItem value="vendor-analysis">Vendor Analysis</SelectItem>
                      <SelectItem value="inheritance-transition">Inheritance Transition</SelectItem>
                      <SelectItem value="recruiter-partnership">Recruiter Partnership</SelectItem>
                      <SelectItem value="general-inquiry">General Inquiry</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Message */}
              <div>
                <label className="text-xs uppercase tracking-widest text-[#64748B] mb-2 block">
                  Additional Details
                </label>
                <Textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Please share any specific concerns or information relevant to your inquiry..."
                  rows={4}
                  className="dossier-input w-full rounded-none border-0 border-b border-[#E2E2E2] focus:border-[#C5A059] bg-transparent px-0 resize-none font-heading"
                  data-testid="input-message"
                />
              </div>

              {/* Privacy Notice */}
              <p className="text-xs text-[#64748B] leading-relaxed">
                Your information is handled with complete confidentiality. We do not share or sell 
                client information to third parties.
              </p>

              {/* Submit Button */}
              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-[#0B1221] text-white hover:bg-[#C5A059] hover:text-[#0B1221] rounded-none px-8 py-6 text-xs uppercase tracking-widest font-bold transition-all duration-300 disabled:opacity-50"
                data-testid="submit-inquiry-btn"
              >
                {isSubmitting ? "Submitting..." : "Submit Confidential Inquiry"}
              </Button>
            </form>
          </div>

          {/* Contact Info */}
          <div className="animate-fade-in-up opacity-0" style={{ animationDelay: "0.2s", animationFillMode: "forwards" }}>
            <div className="sticky top-32">
              <div className="aspect-[4/3] overflow-hidden mb-10">
                <img
                  src={IMAGES.contact}
                  alt="Luxury interior"
                  className="w-full h-full object-cover image-hover-effect"
                />
              </div>

              <div className="space-y-8">
                <div className="p-8 bg-[#F9F9F7] border-l-2 border-[#C5A059]">
                  <h4 className="font-heading text-xl text-[#0B1221] font-medium mb-4">
                    Discretion Assured
                  </h4>
                  <p className="text-[#64748B] text-sm leading-relaxed">
                    All consultations are conducted with the highest level of confidentiality. 
                    We understand the sensitive nature of estate operations and maintain strict 
                    privacy protocols.
                  </p>
                </div>

                <div className="space-y-4">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 flex items-center justify-center border border-[#E2E2E2]">
                      <Mail className="w-5 h-5 text-[#C5A059]" />
                    </div>
                    <div>
                      <p className="text-xs text-[#64748B] uppercase tracking-widest">Email</p>
                      <a href="mailto:inquiries@estatebenchmark.com" className="text-[#0B1221] hover:text-[#C5A059] transition-colors">inquiries@estatebenchmark.com</a>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 flex items-center justify-center border border-[#E2E2E2]">
                      <Phone className="w-5 h-5 text-[#C5A059]" />
                    </div>
                    <div>
                      <p className="text-xs text-[#64748B] uppercase tracking-widest">Phone</p>
                      <a href="tel:+16467702247" className="text-[#0B1221] hover:text-[#C5A059] transition-colors">+1 (646) 770-2247</a>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 flex items-center justify-center border border-[#E2E2E2]">
                      <MapPin className="w-5 h-5 text-[#C5A059]" />
                    </div>
                    <div>
                      <p className="text-xs text-[#64748B] uppercase tracking-widest">Office</p>
                      <p className="text-[#0B1221]">New York, NY</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
    </>
  );
};

// Footer
const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-[#0B1221] py-16" data-testid="footer">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-12">
          {/* Logo & Description */}
          <div className="md:col-span-2">
            <img 
              src={IMAGES.logo} 
              alt="Estate Benchmark Authority" 
              className="h-16 w-auto mb-6 mix-blend-lighten"
            />
            <p className="text-white/50 leading-relaxed max-w-md">
              Independent operational insight for private estates. Providing visibility, 
              accountability, and operational clarity for ultra-high-net-worth properties.
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-xs uppercase tracking-widest text-[#C5A059] font-bold mb-6">
              Quick Links
            </h4>
            <ul className="space-y-3">
              {["About", "Services", "Partners", "Contact"].map((link) => (
                <li key={link}>
                  <button
                    onClick={() => document.querySelector(`#${link.toLowerCase()}`)?.scrollIntoView({ behavior: "smooth" })}
                    className="text-white/60 hover:text-[#C5A059] transition-colors duration-300 text-sm"
                    data-testid={`footer-${link.toLowerCase()}`}
                  >
                    {link}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-xs uppercase tracking-widest text-[#C5A059] font-bold mb-6">
              Contact
            </h4>
            <ul className="space-y-3 text-white/60 text-sm">
              <li>inquiries@estatebenchmark.com</li>
              <li>+1 (646) 770-2247</li>
              <li>New York, NY</li>
            </ul>
          </div>
        </div>

        {/* Divider */}
        <div className="divider-diamond mb-8">
          <span></span>
        </div>

        {/* Bottom Bar */}
        <div className="flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-white/40 text-xs">
            © {currentYear} Estate Benchmark Authority. All rights reserved.
          </p>
          <div className="flex gap-6">
            <button className="text-white/40 hover:text-[#C5A059] text-xs transition-colors duration-300" data-testid="footer-privacy">
              Privacy Policy
            </button>
            <button className="text-white/40 hover:text-[#C5A059] text-xs transition-colors duration-300" data-testid="footer-terms">
              Terms of Service
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
};

// Main HomePage Component
export default function HomePage() {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-[#F9F9F7]" data-testid="home-page">
      <Navigation scrolled={scrolled} />
      <HeroSection />
      <AboutSection />
      <WhoThisIsForSection />
      <ServicesSection />
      <UseCasesSection />
      <PartnersSection />
      <ContactSection />
      <Footer />
    </div>
  );
}
